import { PropertyValueMap, unsafeCSS } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';
import { html } from 'lit/static-html.js';
import { property, state, query } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { FormControlMixin } from '@open-wc/form-control';
import { TinyColor } from '@ctrl/tinycolor';
import {
  customElement,
  FormControl,
  CheckableFormControl,
  FormControlController,
} from '../../base-element';
import { BufferWC } from '../../buffer';
import './color-picker-control.wc';
import '../../popover/src/popover.wc';
import '../../input/input.wc';
import { InputWC } from '../../input';
import { PopoverWC } from '../../popover';
import styles from './color-picker.scss?inline';

@customElement('ps-color-picker')
export class ColorPickerWC
  extends FormControlMixin(BufferWC)
  implements CheckableFormControl
{
  static styles = unsafeCSS(styles);

  // The following properties and methods aren't strictly required,
  // but browser-level form controls provide them. Providing them helps
  // ensure consistency with browser-provided controls.
  getForm(): HTMLFormElement | null {
    return this.FormControlController.getForm();
  }

  // without this, we don't get native-like form field submit, reset, or per-input-field validation automatically triggered
  private readonly FormControlController = new FormControlController(this);

  @query('ps-input') input: InputWC;

  @query('ps-popover') poopver: PopoverWC;

  @state() private popoverActive = false;

  @state() invalid = false;

  /**
   * The current value of the color picker. The value's format will vary based the `format` attribute. To get the value
   * in a specific format, use the `getFormattedValue()` method. The value is submitted as a name/value pair with form
   * data.
   */
  @property({ reflect: true }) value = '';

  /** The default value of the form control. Primarily used for resetting the form control. */
  @property({ reflect: true }) defaultValue = '';

  /** Placeholder text to show as a hint when the input is empty. */
  @property({ reflect: true }) placeholder?: string = '';

  /**
   * The format to use. If opacity is enabled, these will translate to HEXA, RGBA, HSLA, and HSVA respectively. The color
   * picker will accept user input in any format (including CSS color names) and convert it to the desired format.
   */
  @property({ reflect: true }) format: 'hex' | 'rgb' | 'hsl' | 'hsv' = 'hex';

  /** Renders the color picker inline rather than in a dropdown. */
  @property({ type: Boolean, reflect: true }) inline = false;

  /** The name of the form control, submitted as a name/value pair with form data. */
  @property({ reflect: true }) name = '';

  /** Disables the color picker. */
  @property({ type: Boolean, reflect: true }) disabled = false;

  /** Shows the opacity slider. Enabling this will cause the formatted value to be HEXA, RGBA, or HSLA. */
  @property({ type: Boolean, reflect: true }) opacity = false;

  /** Makes the color picker a required field. */
  @property({ type: Boolean, reflect: true }) required = false;

  /** Removes the button that lets users toggle between format.   */
  @property({ attribute: 'no-format-toggle', type: Boolean, reflect: true })
  noFormatToggle = false;

  /**
   * One or more predefined color swatches to display as presets in the color picker. Can include any format the color
   * picker can parse, including HEX(A), RGB(A), HSL(A), HSV(A), and CSS color names. Each color must be separated by a
   * semicolon (`;`). Alternatively, you can pass an array of color values to this property using JavaScript.
   */
  @property({ reflect: true }) swatches: string | string[] = '';

  @property() validationMode: FormControl['validationMode'] = 'onSubmit';

  /**
   * @method
   * @return {boolean} Returns true if internals's target element has no validity
   * problems; otherwise, returns false, fires an invalid event at the element, and (if
   * the event isn't canceled) reports the problem to the user.
   */
  reportValidity() {
    // this is how visible form field show up when a form tries to be submitted
    return this.input?.reportValidity();
  }

  private handleInvalid(event: Event) {
    this.FormControlController.setValidity(false);
    this.FormControlController.emitInvalidEvent(event);
  }

  /** Checks for validity but does not show the browser's validation message. */
  checkValidity() {
    return this.input?.checkValidity();
  }

  setCustomValidity(message: string) {
    this.input?.setCustomValidity(message);
    // this.invalid = !this.checkValidity();

    this.FormControlController.updateValidity();
  }

  /** Gets the validity state object */
  get validity() {
    // When rendered `inline`, ps-color-picker has no <ps-input> in its shadow
    // DOM (inline renders only ps-color-picker-control, no ps-input wrapper).
    // this.input is therefore null, making `this.input?.validity` → undefined.
    // FormControlMixin (_FormControl_onBlur / _FormControl_shouldShowError)
    // then reads `this.validity.valid` — i.e. `undefined.valid` — and crashes
    // with "Cannot read properties of undefined (reading 'valid')".
    // Guard: fall back to the ElementInternals validity (always present via
    // FormControlMixin's `this.internals = this.attachInternals()` in the ctor).
    return this.input?.validity ?? this.internals.validity;
  }

  // this is how all form fields with validation errors get triggered when a form tries to be submitted
  firstUpdated(
    changedProperties: PropertyValueMap<unknown> | Map<PropertyKey, unknown>
  ): void {
    super.firstUpdated(changedProperties);

    this.FormControlController.updateValidity();
  }

  private handleFocus() {
    this.emit('focus');
  }

  private handleBlur() {
    this.emit('blur');
  }

  // PSTUDIO-3866: same inline/null-`input` invariant the `validity` getter
  // documents above — when rendered `inline`, ps-color-picker has no inner
  // <ps-input>, so `this.input` is null. On the Escape→click-outside close,
  // the browser's native focus teardown invokes this overridden `blur()` on the
  // focused host; an unguarded `this.input.blur()` then crashed the page with
  // "Cannot read properties of null (reading 'blur')". Optional-chaining makes
  // both a safe no-op (there is no inner control to focus/blur when inline).
  focus() {
    this.input?.focus();
  }

  blur() {
    this.input?.blur();
  }

  private togglePopoverActive = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
    this.popoverActive = !this.popoverActive;
  };

  // eslint-disable-next-line class-methods-use-this
  private handleInputInput(event: Event) {
    // Prevent the <ps-input>'s sl-input event from bubbling up
    event.stopPropagation();
  }

  private handleInputChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const oldValue = this.value;

    // Prevent the <ps-input>'s sl-change event from bubbling up
    event.stopPropagation();

    const color = new TinyColor(target.value);
    if (!color.isValid) {
      target.value = oldValue;
      return;
    }

    this.value = color.toString();

    this.emit('change');
  }

  private handleColorPickerChange(e: Event) {
    this.value = (e.target as HTMLInputElement).value;
    this.emit('change');
  }

  private handleColorPickerInput(e: Event) {
    this.value = (e.target as HTMLInputElement).value;
    this.emit('input');
  }

  render() {
    const colorPicker = html`
      <ps-color-picker-control
        value=${this.value}
        defaultValue=${this.defaultValue}
        format=${this.format}
        name=${this.name}
        .inline=${this.inline}
        .noFormatToggle=${this.noFormatToggle}
        swatches=${this.swatches}
        .disabled=${this.disabled}
        .required=${this.required}
        .opacity=${this.opacity}
        @change=${this.handleColorPickerChange}
        @input=${this.handleColorPickerInput}
      ></ps-color-picker-control>
    `;

    // Render inline
    if (this.inline) {
      return colorPicker;
    }

    // Render as a dropdown
    return html`
      <div class="c-color-picker-trigger">
        <button
          id="color-picker-trigger"
          aria-label="color"
          class="c-color-picker-trigger__button"
          style=${styleMap({
            backgroundColor: this.value || 'transparent',
          })}
          @click=${this.togglePopoverActive}
        ></button>
        <ps-input
          variant="outlined"
          class="c-color-picker-trigger__input"
          value=${this.value}
          defaultValue=${this.defaultValue}
          placeholder=${ifDefined(this.placeholder)}
          @change=${this.handleInputChange}
          @input=${this.handleInputInput}
          @focus=${this.handleFocus}
          @blur=${this.handleBlur}
          @invalid=${this.handleInvalid}
          name=${this.name}
          ?disabled=${this.disabled}
        ></ps-input>
      </div>

      <ps-popover
        width="auto"
        size="none"
        trigger="color-picker-trigger"
        distance="16"
        disable-trigger-listeners
        .active=${this.popoverActive}
        >${colorPicker}</ps-popover
      >
    `;
  }
}
